/**
 * M-X30c — Calls API client.
 *
 * Companion to apps/chaos-gateway/src/voice-gateway/index.ts. Lists
 * card-scoped calls, lets the founder dial out, abort, and answer
 * HITL questions. Real Postgres LISTEN/NOTIFY SSE fan-out lives at
 * /api/calls/:id/events/stream (see LiveCallView).
 *
 * Backend was shipped in M-X30a + M-X30b; this module is the typed
 * wrapper the UI calls.
 */
import { apiJson, ApiError, apiFetch } from './client';

export type CallStatus =
  | 'initializing'
  | 'dialing'
  | 'ringing'
  | 'active'
  | 'completed'
  | 'failed'
  | 'aborted'
  | 'voicemail';

export type CallVendor = 'anthropic' | 'openai' | 'google';

export interface CallRow {
  id: string;
  cardId: string;
  orgId: string;
  sessionId: string | null;
  provider: string;
  agentRoleIds: string[];
  agentVendor: CallVendor;
  agentModel: string | null;
  toNumber: string;
  fromNumber: string | null;
  direction: 'outbound' | 'inbound';
  status: CallStatus;
  callControlId: string | null;
  conversationId: string | null;
  startedAt: string | null;
  answeredAt: string | null;
  endedAt: string | null;
  durationSec: number | null;
  summaryText: string | null;
  recordingUrl: string | null;
  costUsd: number | null;
  errorMessage: string | null;
  metadata: Record<string, unknown>;
  createdBy: string | null;
  createdAt: string;
}

export interface CallEvent {
  id: string;
  callId?: string;
  eventType: string;
  speaker: 'caller' | 'agent' | 'system' | null;
  textContent: string | null;
  audioOffsetMs: number | null;
  isFinal: boolean;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface CallQuestion {
  id: string;
  questionText: string;
  options: string[] | null;
  answerText: string | null;
  answeredBy: string | null;
  answeredAt: string | null;
  injectedAt: string | null;
  createdAt: string;
  /** M-X31/1: ISO timestamp when this question will time out (null = legacy row). */
  timeoutAt: string | null;
  /** M-X31/1: 'asked' | 'answered' | 'consumed' | 'timed_out' */
  status: string;
}

export interface CallLimitRow {
  scope: 'org' | 'card';
  scopeId: string;
  maxConcurrent: number;
  maxDailyCostUsd: number;
  isDefault: boolean;
}

export interface CallLimitsBundle {
  defaults: { maxConcurrent: number; maxDailyCostUsd: number };
  org: CallLimitRow & { active: number; dailyCostUsd: number };
  card: CallLimitRow & { active: number; dailyCostUsd: number };
}

export interface CallDetail {
  call: CallRow;
  events: CallEvent[];
  questions: CallQuestion[];
  liveStream: Record<string, unknown> | null;
}

export interface CreateCallInput {
  toNumber: string;
  agentRoleIds?: string[];
  agentVendor?: CallVendor;
  agentModel?: string | null;
  fromNumber?: string | null;
  /** Spoken first, word for word, when the call is answered. Optional. */
  openingLine?: string;
}

export interface CreateCallResponse {
  call: CallRow | null;
  /** Present only on error responses (4xx/5xx). */
  error?: string;
}

/** M-X32 — compact grid projection (one row per active/recent call). */
export interface GridTile {
  callId: string;
  toNumber: string;
  fromNumber: string | null;
  status: CallStatus;
  durationSec: number;
  latestLine: string | null;
  latestSpeaker: string | null;
  questionPending: boolean;
  batchId: string | null;
}

export interface GridRecent {
  callId: string;
  toNumber: string;
  status: CallStatus;
  durationSec: number | null;
  batchId: string | null;
  endedAt: string | null;
}

export interface GridBundle {
  active: GridTile[];
  recentlyCompleted: GridRecent[];
  queued: number;
  limits: {
    maxConcurrent: number;
    active: number;
    isDefault: boolean;
  };
}

export interface BatchDispatchResponse {
  batchId: string;
  totalNumbers: number;
  immediatelyDispatched: number;
  queued: number;
  calls: Array<{
    callId: string;
    toNumber: string;
    status: string;
    error: string | null;
  }>;
}

export const callsApi = {
  /** Newest-first list (up to 200) for a card. */
  list(cardId: string): Promise<CallRow[]> {
    return apiJson<{ calls: CallRow[] }>(
      `/cards/${encodeURIComponent(cardId)}/calls`,
    ).then((r) => r.calls);
  },

  /** M-X32 — compact projection optimized for the parallel grid view. */
  grid(cardId: string, opts?: { batchId?: string }): Promise<GridBundle> {
    const qs = opts?.batchId ? `?batchId=${encodeURIComponent(opts.batchId)}` : '';
    return apiJson<GridBundle>(
      `/cards/${encodeURIComponent(cardId)}/calls/grid${qs}`,
    );
  },

  /** M-X32 — dispatch a batch of phone numbers in parallel. */
  batch(cardId: string, input: {
    phoneNumbers: string[];
    commonPrompt?: string;
    kbContext?: string;
    agentVendor?: CallVendor;
    agentModel?: string | null;
    agentRoleIds?: string[];
    label?: string;
    /** One line for every call of the batch, spoken first, word for word. */
    openingLine?: string;
  }): Promise<BatchDispatchResponse> {
    return apiJson<BatchDispatchResponse>(
      `/cards/${encodeURIComponent(cardId)}/calls/batch`,
      { method: 'POST', json: input },
    );
  },

  /**
   * Dispatch a call. The gateway picks Telnyx creds via the M-X9
   * vault → env fallback. Returns the freshly-minted row on success.
   *
   * On failure the gateway sends back {error, call} with a non-2xx
   * status. ApiError carries the status + code; UI maps:
   *   503 telnyx_not_configured  → "Setup Telnyx in Credentials"
   *   429 *_limit_reached        → "Slot full, try again later"
   *   400 *                      → bad input
   */
  create(cardId: string, input: CreateCallInput): Promise<CallRow> {
    return apiJson<{ call: CallRow }>(
      `/cards/${encodeURIComponent(cardId)}/calls`,
      { method: 'POST', json: input },
    ).then((r) => r.call);
  },

  /** Returns the card's call detail with events + questions. */
  get(callId: string): Promise<CallDetail> {
    return apiJson<CallDetail>(`/calls/${encodeURIComponent(callId)}`);
  },

  /** Hangup. Idempotent — already-terminated returns {ok, alreadyTerminated}. */
  abort(callId: string): Promise<{ ok: true; alreadyTerminated?: boolean }> {
    return apiJson<{ ok: true; alreadyTerminated?: boolean }>(
      `/calls/${encodeURIComponent(callId)}/abort`,
      { method: 'POST', json: {} },
    );
  },

  /**
   * Inject a free-form context message into the agent's next turn.
   * `asSystemMessage` (default true) is preserved in the row's
   * metadata so the operator can override the agent's stance.
   */
  inject(callId: string, args: { text: string; asSystemMessage?: boolean }): Promise<{ ok: true }> {
    return apiJson<{ ok: true }>(
      `/calls/${encodeURIComponent(callId)}/inject`,
      {
        method: 'POST',
        json: { text: args.text, asSystemMessage: args.asSystemMessage ?? true },
      },
    );
  },

  /** List of HITL questions for the call (newest first). */
  questions(callId: string): Promise<CallQuestion[]> {
    return apiJson<{ questions: CallQuestion[] }>(
      `/calls/${encodeURIComponent(callId)}/questions`,
    ).then((r) => r.questions);
  },

  /** Answer an open HITL question. Logs `question_answered` event. */
  answerQuestion(callId: string, qid: string, answer: string): Promise<{ ok: true }> {
    return apiJson<{ ok: true }>(
      `/calls/${encodeURIComponent(callId)}/questions/${encodeURIComponent(qid)}/answer`,
      { method: 'POST', json: { answer } },
    );
  },

  /** Per-card + per-org limits + active counts + 24h cost. */
  limits(cardId: string): Promise<CallLimitsBundle> {
    return apiJson<CallLimitsBundle>(
      `/cards/${encodeURIComponent(cardId)}/calls/limits`,
    );
  },

  /** Update card-level limits. Founder / org-admin only. */
  setCardLimits(cardId: string, patch: { maxConcurrent?: number; maxDailyCostUsd?: number }): Promise<{ card: CallLimitRow }> {
    return apiJson<{ card: CallLimitRow }>(
      `/cards/${encodeURIComponent(cardId)}/call-limits`,
      { method: 'PATCH', json: patch },
    );
  },

  /** Update org-level limits. Founder / org-admin only. */
  setOrgLimits(orgId: string, patch: { maxConcurrent?: number; maxDailyCostUsd?: number }): Promise<{ org: CallLimitRow }> {
    return apiJson<{ org: CallLimitRow }>(
      `/orgs/${encodeURIComponent(orgId)}/call-limits`,
      { method: 'PATCH', json: patch },
    );
  },
};

export const callsKeys = {
  all: ['calls'] as const,
  card: (cardId: string) => [...callsKeys.all, 'card', cardId] as const,
  detail: (callId: string) => [...callsKeys.all, 'detail', callId] as const,
  limits: (cardId: string) => [...callsKeys.all, 'limits', cardId] as const,
  questions: (callId: string) => [...callsKeys.all, 'questions', callId] as const,
  /** M-X32 — grid projection (active + recent), per card. */
  grid: (cardId: string, batchId?: string) =>
    [...callsKeys.all, 'grid', cardId, batchId ?? null] as const,
};

/** Used by the inject composer to detect un-recoverable backend errors. */
export function isCallNotConfiguredError(err: unknown): {
  vendor: 'telnyx' | 'deepgram' | 'elevenlabs';
  code: string;
} | null {
  if (!(err instanceof ApiError)) return null;
  if (err.code === 'telnyx_not_configured') return { vendor: 'telnyx', code: err.code };
  if (err.code === 'deepgram_not_configured') return { vendor: 'deepgram', code: err.code };
  if (err.code === 'elevenlabs_not_configured') return { vendor: 'elevenlabs', code: err.code };
  return null;
}

/** Used by the composer to detect 429 limit errors. */
export function isCallLimitError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  return err.status === 429;
}

/** Re-export apiFetch for SSE consumers that need the absolute URL. */
export { apiFetch };
